import React, { useRef, useState } from "react";
import { CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Calendar, CalendarDayButton } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { RangoFecha } from "./presupuestoBusqueda";

function ymd(date: Date) {
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
}

function fechaLocal(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function rangoMes(date: Date): RangoFecha {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { desde: ymd(start), hasta: ymd(end) };
}

function rangoAnio(date: Date): RangoFecha {
  const year = date.getFullYear();
  return { desde: `${year}-01-01`, hasta: `${year}-12-31` };
}

function ordenar(a: Date, b: Date): [Date, Date] {
  return a.getTime() <= b.getTime() ? [a, b] : [b, a];
}

function PresupuestoDayButton(props: React.ComponentProps<typeof CalendarDayButton>) {
  return <CalendarDayButton {...props} data-day={ymd(props.day.date)} />;
}

function diaDesde(target: EventTarget | null): string | null {
  if (!(target instanceof Element)) return null;
  return target.closest("[data-day]")?.getAttribute("data-day") ?? null;
}

export function PresupuestoCalendario({
  onChange,
  onClear,
  clearLabel,
}: {
  onChange: (rango: RangoFecha) => void;
  onClear?: () => void;
  clearLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date>(new Date());
  const [range, setRange] = useState<DateRange | undefined>();
  const dragFrom = useRef<Date | null>(null);
  const moved = useRef(false);

  const elegir = (rango: RangoFecha) => {
    onChange(rango);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="icon" title="Calendario" className="shrink-0">
          <CalendarIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0">
        <div
          onPointerDown={(event) => {
            const iso = diaDesde(event.target);
            if (!iso) return;
            const day = fechaLocal(iso);
            dragFrom.current = day;
            moved.current = false;
            setRange({ from: day, to: day });
          }}
          onPointerMove={(event) => {
            if (!dragFrom.current) return;
            const hit = document.elementFromPoint(event.clientX, event.clientY);
            const iso = diaDesde(hit);
            if (!iso) return;
            const day = fechaLocal(iso);
            if (ymd(day) !== ymd(dragFrom.current)) moved.current = true;
            const [from, to] = ordenar(dragFrom.current, day);
            setRange({ from, to });
          }}
          onPointerUp={() => {
            const from = dragFrom.current;
            dragFrom.current = null;
            if (!from || !range?.from || !range.to) return;
            if (!moved.current) {
              elegir({ desde: ymd(from), hasta: ymd(from) });
              return;
            }
            elegir({ desde: ymd(range.from), hasta: ymd(range.to) });
          }}
        >
          <Calendar
            mode="range"
            month={month}
            onMonthChange={setMonth}
            selected={range}
            onSelect={setRange}
            components={{
              DayButton: PresupuestoDayButton,
              MonthCaption: ({ calendarMonth, displayIndex: _displayIndex, ...captionProps }) => (
                <div {...captionProps}>
                  <button
                    type="button"
                    className="text-sm font-medium capitalize"
                    onClick={() => elegir(rangoMes(calendarMonth.date))}
                  >
                    {calendarMonth.date.toLocaleDateString("es-AR", { month: "long", year: "numeric" })}
                  </button>
                </div>
              ),
            }}
          />
        </div>
        <div className="flex items-center justify-between border-t px-3 py-2">
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => elegir(rangoAnio(month))}
          >
            Año {month.getFullYear()}
          </button>
          {onClear && clearLabel ? (
            <button
              type="button"
              className={cn("text-xs text-muted-foreground hover:text-foreground")}
              onClick={() => {
                onClear();
                setRange(undefined);
                setOpen(false);
              }}
            >
              {clearLabel}
            </button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
